import { Device, Geotab, Trip, User } from "@fairfleet/geotab";
import { hashKey, useQuery } from "@tanstack/react-query";
import { uniqBy } from "lodash";
import sortBy from "lodash/sortBy";
import { useState } from "react";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { useGroupDeviceIds } from "@/hooks/devices/useGroupDeviceIds";
import { retryUnlessDeviceCap } from "@/utilities/error/DeviceCapError";
import {
  fetchTripsComplete,
  GetTrips,
  TripSearchTerm,
} from "@/utilities/query/fetchTripsComplete";

interface Args {
  users?: User[];
  devices?: Device[];
  toDate: Date;
  fromDate: Date;
  enabled?: boolean;
  /**
   * Hands out the trips found so far as `partialData` while the rest still loads, and asks Geotab
   * about fewer vehicles at a time, so the first rows show sooner on a long range.
   */
  progressive?: boolean;
}

/**
 * Searches started at a time in progressive mode. Smaller than the default batch: the first rows
 * arrive sooner, and fewer large answers are open at Geotab and in the tab at once.
 */
const PROGRESSIVE_SEARCHES_PER_BATCH = 10;

/**
 * Every trip in a date range, for the given drivers or vehicles.
 *
 * Complete: each search is paged until the window is covered and the combined result is not
 * capped. It used to stop at a thousand trips per search and a thousand overall — fine for a
 * screen someone scrolls, wrong for a logbook that has to be handed on, because a wider period
 * simply lost rows with nothing to say so.
 */
export function useTripsByDateRange({
  users,
  devices,
  toDate,
  fromDate,
  enabled,
  progressive = false,
}: Args) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { groups, groupFilterCondition } = useAddInState();
  // Searches (vehicles or drivers) done of all: a whole group takes one search per vehicle.
  const [searchProgress, setProgress] = useState<TripsProgress>();
  // Tagged with the query it belongs to, so a new range never shows the old range's rows.
  const [partial, setPartial] = useState<{ key: string; trips: Trip[] }>();
  // Without a driver or vehicle selection the trips come from every vehicle of the group filter.
  // Those ids are their own query so a cap error stays put instead of being re-asked with the trips.
  const needsGroupDevices = !users?.length && !devices?.length;
  const groupDeviceIds = useGroupDeviceIds({ enabled: enabled !== false && needsGroupDevices });

  // The group filter decides which devices are searched, so it belongs in the key — otherwise
  // switching groups served the trips of the previously selected group. Only the group ids go
  // in: React Query re-hashes the key by value on every render, and full group records carry
  // far more than identity.
  const queryKey = [
    "trips",
    activeSessionId,
    {
      userIds: users?.map(u => u.id),
      deviceIds: devices?.map(d => d.id),
      toDate,
      fromDate,
      groupIds: groups?.map(g => g.id),
      groupFilterCondition,
    },
  ];
  const partialKey = hashKey(queryKey);

  const trips = useQuery({
    enabled: enabled !== false && (!needsGroupDevices || groupDeviceIds.data !== undefined),
    queryKey,

    // A full device page is deterministic; retrying would only refetch the same 5000 devices.
    retry: retryUnlessDeviceCap,

    queryFn: async ({ signal }) => {
      // Only a manual refetch (a retry button) gets here without the ids; it asks for them again.
      const ids =
        needsGroupDevices && !groupDeviceIds.data
          ? (await groupDeviceIds.refetch({ throwOnError: true })).data
          : groupDeviceIds.data;
      const searches = searchTerms({ users, devices, groupDeviceIds: ids });

      const fetched = await fetchTripsComplete(getTrips(geotabClient, signal), {
        fromDate,
        toDate,
        searches,
        signal,
        batchSize: progressive ? PROGRESSIVE_SEARCHES_PER_BATCH : undefined,
        // A superseded load keeps running to its next batch; it must not overwrite the new count.
        onProgress: (done, total) => {
          if (!signal?.aborted) setProgress({ done, total });
        },
        onBatch: progressive
          ? collected => {
              if (!signal?.aborted) setPartial({ key: partialKey, trips: orderTrips(collected) });
            }
          : undefined,
      });

      return orderTrips(fetched);
    },
  });

  // Only a running trip load has progress; an aborted one must not show its count for the next.
  const progress = trips.isFetching ? searchProgress : undefined;
  // Only until the complete result is there, and only the rows of the range being loaded.
  const partialData =
    progressive && trips.data === undefined && trips.isFetching && partial?.key === partialKey
      ? partial.trips
      : undefined;
  if (!needsGroupDevices) return { ...trips, progress, partialData };
  // The trips cannot load before the group's vehicles are known, so their state is part of it.
  // Only the fields below are merged; the rest (isRefetching, failureCount, errorUpdatedAt, ...)
  // still describe the trips query alone. No consumer reads them today.
  const status = groupDeviceIds.isError ? "error" : trips.status;
  return {
    ...trips,
    error: groupDeviceIds.error ?? trips.error,
    isError: groupDeviceIds.isError || trips.isError,
    isPending: !groupDeviceIds.isError && trips.isPending,
    isLoading: groupDeviceIds.isLoading || trips.isLoading,
    isFetching: groupDeviceIds.isFetching || trips.isFetching,
    isSuccess: status === "success",
    fetchStatus: groupDeviceIds.isFetching ? "fetching" : trips.fetchStatus,
    status,
    progress,
    partialData,
  } as typeof trips & { progress?: TripsProgress; partialData?: Trip[] };
}

/**
 * Deduplicated because two searches can name the same trip — a driver and the vehicle they drove —
 * and sorted because the pages arrive per search, not in time order.
 */
function orderTrips(trips: readonly Trip[]): Trip[] {
  return uniqBy(
    sortBy(trips, t => t.start),
    t => t.id,
  );
}

function getTrips(geotabClient: Geotab, signal?: AbortSignal): GetTrips {
  return (search, resultsLimit) =>
    geotabClient.get("Trip", search, resultsLimit, signal) as Promise<Trip[]>;
}

export interface TripsProgress {
  done: number;
  total: number;
}

interface Selection {
  users?: User[];
  devices?: Device[];
  groupDeviceIds?: string[];
}

/**
 * What to ask Geotab about: one search per driver, per vehicle, or per vehicle of the group.
 */
function searchTerms({ users, devices, groupDeviceIds = [] }: Selection): TripSearchTerm[] {
  if (users && users.length > 0) {
    return users.map(u => ({ userSearch: { id: u.id } }));
  }

  if (devices && devices.length > 0) {
    return devices.map(d => ({ deviceSearch: { id: d.id } }));
  }

  return groupDeviceIds.map(id => ({ deviceSearch: { id } }));
}
